import * as THREE from 'three';
import { PALETTE } from '@/game/theme';

/**
 * The look: environment lighting, PBR materials, and the custom track shader.
 *
 * Three things separate a 3D scene that looks generated from one that looks art-directed, and all
 * three live here:
 *
 *  1. IMAGE-BASED LIGHTING. Flat lights make everything read as plastic. A PBR material needs an
 *     environment to reflect, and the environment is what puts the brand's colours into every
 *     highlight. Built procedurally from the palette — no HDR file to download, so it stays offline.
 *  2. MATERIAL CONTRAST. Rough track, semi-metallic player, emissive collectibles. If everything has
 *     the same roughness the scene reads as one plastic mass regardless of colour.
 *  3. A SIGNATURE SHADER. The scrolling track grid is a custom ShaderMaterial rather than a texture,
 *     so speed is expressed in the surface itself and it costs no memory.
 */

/**
 * Build an environment map from the brand palette by PMREM-ing a tiny scene of coloured emissive
 * panels — a sky dome, a warm horizon and a cool fill. Cheap, deterministic, offline-safe.
 */
export function createEnvironment(renderer: THREE.WebGLRenderer): THREE.Texture {
	const pmrem = new THREE.PMREMGenerator(renderer);
	pmrem.compileEquirectangularShader();

	const envScene = new THREE.Scene();
	const panel = (color: string, intensity: number, position: [number, number, number], scale: [number, number, number]) => {
		const mesh = new THREE.Mesh(
			new THREE.BoxGeometry(1, 1, 1),
			new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity) }),
		);
		mesh.position.set(...position);
		mesh.scale.set(...scale);
		envScene.add(mesh);
		return mesh;
	};

	// Sky above, warm horizon in front, cool bounce behind — a three-point rig, as a room.
	panel(PALETTE.skyTop, 1.4, [0, 8, 0], [24, 1, 24]);
	panel(PALETTE.skyLow, 3.2, [0, 1.5, -12], [24, 6, 1]);
	panel(PALETTE.skyMid, 1.1, [0, 2, 12], [24, 8, 1]);
	panel(PALETTE.orb, 1.6, [-10, 3, 0], [1, 8, 24]);
	panel(PALETTE.playerGlow, 1.2, [10, 3, 0], [1, 8, 24]);

	const target = pmrem.fromScene(envScene, 0.04);

	// The source scene has done its job; only the prefiltered cube map is kept.
	envScene.traverse((obj) => {
		if (obj instanceof THREE.Mesh) {
			obj.geometry.dispose();
			(obj.material as THREE.Material).dispose();
		}
	});
	pmrem.dispose();

	return target.texture;
}

/** Vertical gradient sky, drawn once to a canvas and used as the scene background. */
export function createSkyTexture(): THREE.Texture {
	const canvas = document.createElement('canvas');
	canvas.width = 4;
	canvas.height = 256;
	const g = canvas.getContext('2d')!;
	const grad = g.createLinearGradient(0, 0, 0, 256);
	grad.addColorStop(0, PALETTE.skyTop);
	grad.addColorStop(0.55, PALETTE.skyMid);
	grad.addColorStop(1, PALETTE.skyLow);
	g.fillStyle = grad;
	g.fillRect(0, 0, 4, 256);
	const texture = new THREE.CanvasTexture(canvas);
	texture.colorSpace = THREE.SRGBColorSpace;
	return texture;
}

/** Soft radial falloff for the player's contact shadow — reads as contact where a hard disc does not. */
export function createShadowTexture(): THREE.Texture {
	const canvas = document.createElement('canvas');
	canvas.width = canvas.height = 64;
	const g = canvas.getContext('2d')!;
	const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
	grad.addColorStop(0, 'rgba(0,0,0,0.6)');
	grad.addColorStop(0.55, 'rgba(0,0,0,0.28)');
	grad.addColorStop(1, 'rgba(0,0,0,0)');
	g.fillStyle = grad;
	g.fillRect(0, 0, 64, 64);
	return new THREE.CanvasTexture(canvas);
}

/**
 * The track surface: a shader that scrolls a lane grid and pulses with speed.
 *
 * Written as a ShaderMaterial rather than a scrolling texture because the lines stay crisp at every
 * distance (no mip blur), the horizon fade is computed per-pixel, and speed can drive the emission
 * directly. `uScroll` and `uSpeed` are updated each frame by the Runner.
 */
export function createTrackMaterial(): THREE.ShaderMaterial {
	return new THREE.ShaderMaterial({
		uniforms: {
			uScroll: { value: 0 },
			uSpeed: { value: 0 },
			uBase: { value: new THREE.Color(PALETTE.track) },
			uLine: { value: new THREE.Color(PALETTE.laneLine) },
			uGlow: { value: new THREE.Color(PALETTE.orb) },
			uFogColor: { value: new THREE.Color(PALETTE.fog) },
			uFogNear: { value: 18 },
			uFogFar: { value: 78 },
		},
		vertexShader: /* glsl */ `
			varying vec2 vUv;
			varying float vDepth;
			void main() {
				vUv = uv;
				vec4 mv = modelViewMatrix * vec4(position, 1.0);
				vDepth = -mv.z;
				gl_Position = projectionMatrix * mv;
			}
		`,
		fragmentShader: /* glsl */ `
			uniform float uScroll;
			uniform float uSpeed;
			uniform vec3 uBase;
			uniform vec3 uLine;
			uniform vec3 uGlow;
			uniform vec3 uFogColor;
			uniform float uFogNear;
			uniform float uFogFar;
			varying vec2 vUv;
			varying float vDepth;

			void main() {
				// Lane seams: bands that do not move, so lanes stay legible at any speed.
				float lane = abs(fract(vUv.x * 3.0) - 0.5);
				float seam = smoothstep(0.46, 0.5, lane);

				// Rungs scrolling toward the camera: this is what makes speed readable.
				float rung = fract(vUv.y * 14.0 + uScroll);
				float rungLine = smoothstep(0.88, 1.0, rung);

				// A surface, not a void: lift the base and let it fall off toward the horizon, so the
				// road reads as lit ground rather than a hole cut in the sky.
				float nearness = 1.0 - smoothstep(0.0, 0.75, vUv.y);
				vec3 color = mix(uBase * 0.85, uBase * 2.6, nearness);

				// Outer rails: a bright edge stops the track floating free of the terrain beside it.
				float edge = smoothstep(0.47, 0.5, abs(vUv.x - 0.5));
				color = mix(color, uGlow, edge * 0.55);

				color = mix(color, uLine * 1.6, seam * 0.7);
				// Faster travel pushes the rungs toward the accent colour and brightens them.
				color = mix(color, mix(uLine * 1.8, uGlow, uSpeed), rungLine * (0.45 + uSpeed * 0.55));

				// Distance fade, matched to the scene fog so the track never floats free of the world.
				float fog = smoothstep(uFogNear, uFogFar, vDepth);
				color = mix(color, uFogColor, fog);

				gl_FragColor = vec4(color, 1.0);
			}
		`,
	});
}

/** PBR materials. Contrast in roughness and metalness is what stops the scene reading as plastic. */
export function createMaterials(environment: THREE.Texture) {
	const player = new THREE.MeshStandardMaterial({
		color: PALETTE.player,
		roughness: 0.25,
		metalness: 0.55,
		emissive: new THREE.Color(PALETTE.playerGlow),
		emissiveIntensity: 0.45,
		envMap: environment,
		envMapIntensity: 1.4,
	});

	const hazard = new THREE.MeshStandardMaterial({
		color: PALETTE.hazard,
		roughness: 0.55,
		metalness: 0.35,
		emissive: new THREE.Color(PALETTE.hazardEdge),
		emissiveIntensity: 0.5,
		envMap: environment,
		envMapIntensity: 0.9,
	});

	// Collectibles are pure emission: they must read as light sources for bloom to catch them.
	const orb = new THREE.MeshStandardMaterial({
		color: PALETTE.orb,
		roughness: 0.1,
		metalness: 0,
		emissive: new THREE.Color(PALETTE.orb),
		emissiveIntensity: 2.4,
		envMap: environment,
	});

	const scenery = new THREE.MeshStandardMaterial({
		color: PALETTE.ridgeFar,
		roughness: 0.95,
		metalness: 0.05,
		envMap: environment,
		envMapIntensity: 0.6,
	});

	const shield = new THREE.MeshPhysicalMaterial({
		color: PALETTE.orb,
		roughness: 0.05,
		metalness: 0,
		transmission: 0.85,
		thickness: 0.6,
		transparent: true,
		opacity: 0.55,
		envMap: environment,
	});

	return { player, hazard, orb, scenery, shield };
}
